import React, { useEffect } from 'react';
import { xAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

// Helper to generate a random alphanumeric string for PKCE (debugging)
function generateRandomString(length = 64) {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const values = window.crypto.getRandomValues(new Uint8Array(length));
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length];
  }
  return result;
}

export default function XOAuthCallback() {
  const { showError } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    let state = params.get('state');
    let clientId = params.get('client_id');
    let code_verifier = null;

    console.log('[XOAuthCallback] URL params:', {
      code,
      error,
      errorDescription,
      clientId,
      state,
    });

    const finish = () => {
      if (window.opener) {
        window.opener.postMessage({ type: 'x_connected' }, '*');
        window.close();
      }
    };

    // Helper to base64url encode a string
    function base64urlencode(str) {
      return btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    }

    // Helper to create a code challenge from a code verifier
    async function pkceChallengeFromVerifier(verifier) {
      const encoder = new TextEncoder();
      const data = encoder.encode(verifier);
      const digest = await window.crypto.subtle.digest('SHA-256', data);
      return base64urlencode(digest);
    }

    if (error) {
      showError(`X connection failed: ${errorDescription || error}`);
      finish();
      return;
    }

    // If we have code and state, finish the OAuth flow
    if (code && state) {
      try {
        const stateObj = JSON.parse(decodeURIComponent(state));
        clientId = stateObj.client_id;
        code_verifier = stateObj.code_verifier;
      } catch (e) {
        // fallback: treat state as clientId
        code_verifier = null;
      }
      console.log(
        '[XOAuthCallback] About to call backend with code:',
        code,
        'clientId:',
        clientId,
        'code_verifier:',
        code_verifier
      );
      xAPI
        .exchangeXCode(code, clientId, code_verifier)
        .then((resp) => {
          console.log('[XOAuthCallback] Backend response:', resp);
          finish();
        })
        .catch((err) => {
          console.error('[XOAuthCallback] Backend error:', err);
          finish();
        });
      return;
    }

    // If we only have client_id, start the OAuth flow
    if (clientId && !code) {
      const code_verifier = generateRandomString(64);
      pkceChallengeFromVerifier(code_verifier).then((code_challenge) => {
        const stateObj = {
          client_id: clientId,
          code_verifier,
        };
        const stateParam = encodeURIComponent(JSON.stringify(stateObj));
        console.log(
          '[XOAuthCallback] code_verifier:',
          code_verifier,
          'code_challenge:',
          code_challenge,
          'state:',
          stateParam
        );
        xAPI
          .getXAuthUrl(clientId, code_challenge, stateParam)
          .then((authUrl) => {
            console.log('[XOAuthCallback] X OAuth URL:', authUrl);
            window.location.href = authUrl;
          })
          .catch((err) => {
            showError('Failed to get X OAuth URL');
            console.error('[XOAuthCallback] Error fetching X OAuth URL:', err);
            finish();
          });
      });
      return;
    }

    finish();
  }, []);

  return <div>Connecting your X account...</div>;
}
